/* Copyright (c) 2019 Josh Sanderson */

import EncoderMp3 from './encoder-mp3-worker.js'

class RecorderService {
  constructor(baseUrl) {
    this.baseUrl = baseUrl;

    window.AudioContext = window.AudioContext || window.webkitAudioContext;

    this.em = document.createDocumentFragment();

    this.state = 'inactive';

    this.chunks = [];
    this.chunkType = '';

    this.encoderMimeType = 'audio/mpeg';

    this.config = {
      broadcastAudioProcessEvents: false,
      createAnalyserNode: false,
      createDynamicsCompressorNode: false,
      forceScriptProcessor: false,
      manualEncoderId: 'mp3',
      micGain: 1.0,
      processorBufferSize: 2048,
      stopTracksAndCloseCtxWhenFinished: true,
      //usingMediaRecorder: typeof window.MediaRecorder !== 'undefined',
      usingMediaRecorder: true,
      enableEchoCancellation: true
    }
    if (process.env.NODE_ENV === 'production') {
      this.BASE_URL = 'https://ecprime-shadowing.englishcompany.jp/';
    } else {
      this.BASE_URL = 'http://localhost:3000/';
    }
  }

  createWorker(fn) {
    var js = fn
      .toString()
      .replace(/^function\s*\(\)\s*{/, '')
      .replace(/}$/, '')
    var blob = new Blob([js]);
    return new Worker(URL.createObjectURL(blob));
  }

  startRecording(timeslice) {
    if (this.state !== 'inactive') {
      return;
    }

    if (!navigator || !navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      alert('Missing support for navigator.mediaDevices.getUserMedia') // temp: helps when testing for strange issues on ios/safari
      return;
    }

    this.audioCtx = new AudioContext();
    this.micGainNode = this.audioCtx.createGain();
    this.outputGainNode = this.audioCtx.createGain();

    if (this.config.createDynamicsCompressorNode) {
      this.dynamicsCompressorNode = this.audioCtx.createDynamicsCompressor();
    }

    if (this.config.createAnalyserNode) {
      this.analyserNode = this.audioCtx.createAnalyser();
    }

    if (this.config.forceScriptProcessor || this.config.broadcastAudioProcessEvents || !this.config.usingMediaRecorder) {
      this.processorNode = this.audioCtx.createScriptProcessor(this.config.processorBufferSize, 1, 1); // TODO: Get the number of channels from mic
    }

    if (this.audioCtx.createMediaStreamDestination) {
      this.destinationNode = this.audioCtx.createMediaStreamDestination();
    }
    else {
      this.destinationNode = this.audioCtx.destination;
    }

    if (!this.config.usingMediaRecorder) {
      this.encoderWorker = this.createWorker(EncoderMp3);
      this.encoderWorker.postMessage(['init', { baseUrl: this.BASE_URL, sampleRate: this.audioCtx.sampleRate }]);
      this.encoderMimeType = 'audio/mpeg';
      this.encoderWorker.addEventListener('message', (e) => {
        let event = new Event('dataavailable');
        if (this.config.manualEncoderId === 'ogg') {
          event.data = e.data;
        }
        else {
          event.data = new Blob(e.data, { type: this.encoderMimeType });
        }
        this._onDataAvailable(event);
      })
    }

    const userMediaConstraints = {
      audio: {
        echoCancellation: this.config.enableEchoCancellation
      }
    }
    if (this.config.deviceId) {
      userMediaConstraints.audio.deviceId = this.config.deviceId;
    }

    return navigator.mediaDevices.getUserMedia(userMediaConstraints)
      .then((stream) => {
        this._startRecordingWithStream(stream, timeslice);
      })
      .catch((error) => {
        alert('Error with getUserMedia: ' + error.message);
        console.log(error);
      })
  }

  setMicGain(newGain) {
    this.config.micGain = newGain;
    if (this.audioCtx && this.micGainNode) {
      this.micGainNode.gain.setValueAtTime(newGain, this.audioCtx.currentTime);
    }
  }

  _startRecordingWithStream(stream, timeslice) {
    this.micAudioStream = stream;

    this.inputStreamNode = this.audioCtx.createMediaStreamSource(this.micAudioStream);
    this.audioCtx = this.inputStreamNode.context;

    if (this.onGraphSetupWithInputStream) {
      this.onGraphSetupWithInputStream(this.inputStreamNode);
    }

    this.inputStreamNode.connect(this.micGainNode);
    this.micGainNode.gain.setValueAtTime(this.config.micGain, this.audioCtx.currentTime);

    let nextNode = this.micGainNode;
    if (this.dynamicsCompressorNode) {
      this.micGainNode.connect(this.dynamicsCompressorNode);
      nextNode = this.dynamicsCompressorNode;
    }

    this.state = 'recording';

    if (this.processorNode) {
      nextNode.connect(this.processorNode);
      this.processorNode.connect(this.outputGainNode);
      this.processorNode.onaudioprocess = (e) => this._onAudioProcess(e);
    }
    else {
      nextNode.connect(this.outputGainNode);
    }

    if (this.analyserNode) {
      nextNode.connect(this.analyserNode);
    }

    this.outputGainNode.connect(this.destinationNode);

    if (this.config.usingMediaRecorder) {
      this.mediaRecorder = new MediaRecorder(this.destinationNode.stream);
      this.mediaRecorder.addEventListener('dataavailable', (evt) => this._onDataAvailable(evt));
      this.mediaRecorder.addEventListener('error', (evt) => this._onError(evt));

      this.mediaRecorder.start(timeslice);
    }
    else {
      this.outputGainNode.gain.setValueAtTime(0, this.audioCtx.currentTime);

      if (timeslice) {
        console.log('Time slicing without MediaRecorder is not yet supported. The resulting recording will not be playable.');
        this.slicing = setInterval(function () {
          if (this.state === 'recording') {
            this.encoderWorker.postMessage(['dump', this.context.sampleRate]);
          }
        }, timeslice);
      }
    }
  }

  _onAudioProcess(e) {
    if (this.config.broadcastAudioProcessEvents) {
      this.em.dispatchEvent(new CustomEvent('onaudioprocess', {
        detail: {
          inputBuffer: e.inputBuffer,
          outputBuffer: e.outputBuffer
        }
      }))
    }
    if (!this.config.usingMediaRecorder) {
      if (this.state === 'recording') {
        if (this.config.broadcastAudioProcessEvents) {
          this.encoderWorker.postMessage(['encode', e.outputBuffer.getChannelData(0)]);
        }
        else {
          this.encoderWorker.postMessage(['encode', e.inputBuffer.getChannelData(0)]);
        }
      }
    }
  }

  stopRecording() {
    if (this.state === 'inactive') {
      return;
    }
    if (this.config.usingMediaRecorder) {
      this.state = 'inactive';
      this.mediaRecorder.stop();
    }
    else {
      this.state = 'inactive';
      this.encoderWorker.postMessage(['dump', this.audioCtx.sampleRate]);
      clearInterval(this.slicing);
    }
  }

  load(blob) {
    return new Promise((resolve, reject) => {
      var fileReader = new FileReader();
      fileReader.onload = () => {
        resolve(fileReader.result);
      }
      fileReader.readAsDataURL(blob);
    })
  }

  async _onDataAvailable(evt) {
    this.chunks.push(evt.data);
    this.chunkType = evt.data.type;

    if (this.state !== 'inactive') {
      return;
    }

    let blob = new Blob(this.chunks, { 'type': this.chunkType });
    let blobUrl = await this.load(blob);

    const recording = {
      ts: new Date().getTime(),
      blobUrl: blobUrl,
      mimeType: blob.type,
      size: blob.size
    }

    this.chunks = [];
    this.chunkType = null;

    if (this.destinationNode) {
      this.destinationNode.disconnect();
      this.destinationNode = null;
    }
    if (this.outputGainNode) {
      this.outputGainNode.disconnect();
      this.outputGainNode = null;
    }
    if (this.analyserNode) {
      this.analyserNode.disconnect();
      this.analyserNode = null;
    }
    if (this.processorNode) {
      this.processorNode.disconnect();
      this.processorNode = null;
    }
    if (this.encoderWorker) {
      this.encoderWorker.postMessage(['close']);
      this.encoderWorker = null;
    }
    if (this.dynamicsCompressorNode) {
      this.dynamicsCompressorNode.disconnect();
      this.dynamicsCompressorNode = null;
    }
    if (this.micGainNode) {
      this.micGainNode.disconnect();
      this.micGainNode = null;
    }
    if (this.inputStreamNode) {
      this.inputStreamNode.disconnect();
      this.inputStreamNode = null;
    }

    if (this.config.stopTracksAndCloseCtxWhenFinished) {
      this.micAudioStream.getTracks().forEach((track) => track.stop());
      this.micAudioStream = null;

      this.audioCtx.close();
      this.audioCtx = null;
    }

    this.em.dispatchEvent(new CustomEvent('recording', { detail: { recording: recording } }));
  }

  _onError(evt) {
    console.log('error', evt);
    this.em.dispatchEvent(new Event('error'));
    alert('error:' + evt); // for debugging purposes
  }
}

export { RecorderService };